import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCountdown } from "@/components/edition/category/hooks/useCountdown";
import { planListClicked } from "@/lib/mixpanels/mixpanel";
import { cn } from "@/lib/utils";
import { PASSES_UPGRADE } from "@/settings/constant";
import { Tag, Typography } from "@bookips/sds";
import Image from "next/image";
import { useRouter } from "next/router";
import { Fragment } from "react";
import { SubjectsWithString } from "src/api/product";
/**
 * URL 기반 Position 생성 (예: '/passes/teacher?subject=EN' → '이용권 구매 페이지(선생님) - 영어 탭')
 */ const getPositionFromRoute = (pathname, subject)=>{
    const userType = pathname.includes("teacher") ? "선생님" : pathname.includes("student") ? "학생" : "사용자";
    const subjectCode = Array.isArray(subject) ? subject[0] : subject;
    const subjectName = subjectCode && SubjectsWithString[subjectCode] || "";
    return "이용권 구매 페이지(".concat(userType, ")").concat(subjectName ? " - ".concat(subjectName, " 탭") : "");
};
/**
 * 카운트다운 표시 텍스트를 생성
 * - 24시간 초과: "N일 N시간 N분"
 * - 24시간 이내: "N시간 N분" 또는 "N분"
 */ const useCountdownDisplay = (deadline)=>{
    const { hours , minutes  } = useCountdown(deadline);
    const totalHours = hours;
    const days = Math.floor(totalHours / 24);
    const remainHours = totalHours % 24;
    if (totalHours <= 0 && minutes <= 0) return null;
    const parts = [];
    if (days > 0) {
        parts.push({
            value: days,
            unit: "일"
        });
        parts.push({
            value: remainHours,
            unit: "시간"
        });
        parts.push({
            value: minutes,
            unit: "분"
        });
    } else if (totalHours > 0) {
        parts.push({
            value: totalHours,
            unit: "시간"
        });
        parts.push({
            value: minutes,
            unit: "분"
        });
    } else {
        parts.push({
            value: minutes,
            unit: "분"
        });
    }
    return parts;
};
const UpsellingCard = (param)=>{
    let { plan , className , planOrder =1  } = param;
    const router = useRouter();
    const deadline = plan.useTimer ? plan.expiredAt : null;
    const countdownParts = useCountdownDisplay(deadline);
    const hasTimer = !!deadline && !!countdownParts;
    // 실제 Timer Text 계산
    const getTimerText = ()=>{
        if (!hasTimer || !countdownParts || countdownParts.length === 0) return null;
        return "종료까지 ".concat(countdownParts.map((p)=>"".concat(p.value).concat(p.unit)).join(" "), " 남음");
    };
    const handleClick = ()=>{
        // mx-391: Plan List Clicked
        planListClicked({
            "Plan Id": plan.id,
            "Plan Title": plan.title,
            "Plan SubTitle": plan.body || null,
            "Accent Badge Name": plan.accentTagText || null,
            "Basic Badge Name": plan.basicTagText || null,
            "Timer Text": getTimerText(),
            Image: plan.planPromotionImageUrl || "",
            "Plan Order": planOrder,
            Position: getPositionFromRoute(router.pathname, router.query.subject)
        });
        router.push("".concat(PASSES_UPGRADE, "?planId=").concat(plan.id));
    };
    return /*#__PURE__*/ _jsxs("button", {
        type: "button",
        onClick: handleClick,
        className: cn("flex w-full overflow-hidden rounded-md border border-gray-100 bg-white text-left", "flex-row max-md:w-[240px] max-md:shrink-0 max-md:flex-col", className),
        children: [
            plan.planPromotionImageUrl && /*#__PURE__*/ _jsx("div", {
                className: "relative hidden h-[103px] w-full shrink-0 max-md:block",
                children: /*#__PURE__*/ _jsx(Image, {
                    src: plan.planPromotionImageUrl,
                    alt: plan.title,
                    fill: true,
                    className: "object-cover",
                    unoptimized: true
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: cn("flex flex-1 flex-col justify-center gap-4 self-stretch py-6 pr-4 pl-6", "max-md:h-[185px] max-md:flex-none max-md:justify-between max-md:gap-3 max-md:p-4"),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-col gap-3 max-md:gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-1",
                                children: [
                                    plan.accentTagText && /*#__PURE__*/ _jsxs(Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Tag, {
                                                accent: true,
                                                size: "md",
                                                color: "red",
                                                bold: true,
                                                className: "max-md:hidden",
                                                children: plan.accentTagText
                                            }),
                                            /*#__PURE__*/ _jsx(Tag, {
                                                accent: true,
                                                size: "sm",
                                                color: "red",
                                                bold: true,
                                                className: "hidden max-md:inline-flex",
                                                children: plan.accentTagText
                                            })
                                        ]
                                    }),
                                    plan.basicTagText && /*#__PURE__*/ _jsxs(Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx(Tag, {
                                                color: "gray",
                                                size: "md",
                                                bold: true,
                                                className: "max-md:hidden",
                                                children: plan.basicTagText
                                            }),
                                            /*#__PURE__*/ _jsx(Tag, {
                                                color: "gray",
                                                size: "sm",
                                                bold: true,
                                                className: "hidden max-md:inline-flex",
                                                children: plan.basicTagText
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-col gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "h6",
                                        className: "typo-subtitle1 md:typo-h6 text-title line-clamp-2 break-keep",
                                        children: plan.title
                                    }),
                                    plan.body && /*#__PURE__*/ _jsx(Typography, {
                                        variant: "body2",
                                        className: "text-secondary line-clamp-2 break-keep",
                                        children: plan.body
                                    })
                                ]
                            })
                        ]
                    }),
                    hasTimer && /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-1.5 whitespace-nowrap",
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "subtitle2",
                                className: "text-status-error max-md:!text-sm",
                                children: "종료까지"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "flex items-center gap-1",
                                children: countdownParts.map((part)=>/*#__PURE__*/ _jsxs(Typography, {
                                        variant: "subtitle1",
                                        className: "text-status-error max-md:!text-sm",
                                        children: [
                                            part.value,
                                            part.unit
                                        ]
                                    }, part.unit))
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "subtitle1",
                                className: "text-status-error max-md:!text-sm",
                                children: "남음"
                            })
                        ]
                    })
                ]
            }),
            plan.planPromotionImageUrl && /*#__PURE__*/ _jsx("div", {
                className: "relative h-[232px] w-[540px] shrink-0 max-md:hidden",
                children: /*#__PURE__*/ _jsx(Image, {
                    src: plan.planPromotionImageUrl,
                    alt: plan.title,
                    fill: true,
                    className: "object-cover",
                    unoptimized: true
                })
            })
        ]
    });
};
export default UpsellingCard;
